import { DefaultOptionType } from '@bugly/common';
import { BaseOptionType } from 'antd/lib/select';
import { ReactNode } from 'react';
import i18n from '@/i18n';
import {
  getPlatform,
  getProductId,
  isAndroid,
  isAppleTV,
  isHippy,
  isIOS,
  isVisionOS,
} from '@/infrastructure/product-info';
import { MatchMode } from '@/presentation/components/form/standards/types';
import { hintIconText, hintText } from '@/presentation/components/hint-text/hintText';
import {
  getTips,
  HARMONY_EXIT_REASON_ABILITY_NOT_RESPONDING_TIPS,
  HARMONY_EXIT_REASON_APP_FREEZE_TIPS,
  HARMONY_EXIT_REASON_CPP_CRASH_TIPS,
  HARMONY_EXIT_REASON_JS_ERROR_TIPS,
  HARMONY_EXIT_REASON_NORMAL_TIPS,
  HARMONY_EXIT_REASON_PERFORMANCE_CONTROL_TIPS,
  HARMONY_EXIT_REASON_RESOURCE_CONTROL_TIPS,
  HARMONY_EXIT_REASON_SIGNAL_TIPS,
  HARMONY_EXIT_REASON_UNKNOWN_TIPS,
  HARMONY_EXIT_REASON_UPGRADE_TIPS,
  HARMONY_EXIT_REASON_USER_REQUEST_TIPS,
  IMPORTANCE_BACKGROUND_TIPS,
  IMPORTANCE_CANT_SAVE_STATE_TIPS,
  IMPORTANCE_EMPTY_TIPS,
  IMPORTANCE_FOREGROUND_SERVICE_TIPS,
  IMPORTANCE_FOREGROUND_TIPS,
  IMPORTANCE_GONE_TIPS,
  IMPORTANCE_PERCEPTIBLE_PRE_26_TIPS,
  IMPORTANCE_PERCEPTIBLE_TIPS,
  IMPORTANCE_SERVICE_TIPS,
  IMPORTANCE_TOP_SLEEPING_PRE_28_TIPS,
  IMPORTANCE_TOP_SLEEPING_TIPS,
  IMPORTANCE_VISIBLE_TIPS,
  launchIdText,
  processLaunchIdText,
  PROVIDER_IN_USE_TIPS,
  SERVICE_IN_USE_TIPS,
  STATE_ACTIVE_TIPS,
  STATE_BACKGROUND_TIPS,
  STATE_CREATE_TIPS,
  STATE_DESTROY_TIPS,
  STATE_FOREGROUND_TIPS,
  UNKNOWN_TIPS,
} from '@/presentation/components/issue-list/components/tips';
import { getColorLabel } from '@/presentation/pages/setting/tag-manage/columns';
import { TAG_COLOR_INFO } from '@/presentation/pages/setting/tag-manage/config';
import { stores } from '@/presentation/store';
import { Architecture } from '@/proto/exit';
import { MetricType } from '@/proto/metric';
import { IssueType } from './common';
import { BindingStatus, ResourceStatus } from './resource-manager';
import { KeyDataType } from './token';
export const FIELD_ALL = '';

// "无标签问题"的特殊值
export const NO_TAG_VALUE = 'NO_TAG';

// "无处理人"的特殊值（仅在问题列表上下文使用）
export const NO_HANDLER_VALUE = '__NO_HANDLER__';

export const EMPTY_USER = 'empty_user';

export type Field = Record<string, string[]>;


export interface OptionType extends BaseOptionType {
  label: ReactNode;
  value?: string | number | null;
  [key: string]: any;
  type?: KeyDataType;
}

export interface Option {
  value: string;
  label: string;
  width?: number;
  withoutModeSelection?: boolean;
  type?: KeyDataType;
  valueFormatter?: (value: any) => any;
}

export interface ExOption {
  value: any;
  label: string | ReactNode
}

export enum Zone {
  ResourceStatus = 'resource_status',
  ResourceId = 'resource_id',
  NetworkDiagnose='network_diagnose',
  MetrickitFoom ='metrickit_foom',
  ResourceLeak = 'resource_leak',
  HighCpuStack='high_cpu_stack',
  // 质量报告
  Dau = 'dau',
  // Electron相关
  ElectronJs = 'electron_js',
  ElectronJsPromiseError = 'electron_js_promise_error',
  ElectronSession = 'electron_session',
  ElectronWhiteScreen = 'electron_blank_screen',
  ElectronPv  = 'electron_pv',
  ElectronPerfPage = 'electron_perf_page', // 页面性能
  ElectronLoadPage = 'electron_load_page', // 页面加载
  ElectronPerf = 'electron_perf',
  ElectronApi = 'electron_api', // api监控
  ElectronEvent = 'electron_event', // 自定义事件
  ElectronCustom = 'electron_custom', // 自定义测速
  ElectronStatic = 'electron_static', // 静态资源
  ElectronBrowser = 'electron_browser', // 主进程性能
  ElectronLog = 'electron_log',
  // Hippy、Kuikly共用
  RumStatic = 'rum_static',
  RumApi = 'rum_api',
  RumPerformancePage = 'rum_perf_page', // 页面性能
  RumPerformanceLaunch = 'rum_perf_launch', // 启动性能
  RumPerformanceRunning = 'rum_perf_running', // 运行性能
  RumPerf = 'rum_perf',
  RumPv = 'rum_pv',
  RumJs = 'rum_js',
  RumError = 'rum_log',
  RumEvent = 'rum_event',
  RumCustom = 'rum_custom',
  RumBlankScreen = 'rum_blank_screen',
  ReactNativeStatic = 'react_native_static',
  ReactNativeCustom = 'react_native_custom',
  ReactNativeJs = 'react_native_js',
  ReactNativeError = 'react_native_log',
  ReactNativeEvent = 'react_native_event',
  ReactNativePv = 'react_native_pv',
  MemDetail = 'mem_detail',
  Network = 'network',
  Exception = 'exception',
  BatteryMetric = 'battery_metric',
  BatteryElementMetric = 'battery_element_metric',
  Net = 'traffic',
  Foom = 'foom',
  HighMemory = 'high_memory',
  FoomMalloc = 'foom_malloc',
  BigPic = 'big_bitmap',
  Lag = 'looper_stack',
  Compliance = 'api',
  ComplianceFunctionInvoke = 'func_invoke',
  ComplianceDownload = 'download_monitor',
  ComplianceSelfLaunch = 'self_launch',
  ComplianceNetwork = 'network_capture',
  ComplianceModules = 'permission',
  ComplianceFileAccess = 'file_access',
  CompliancePrivacyFlare = 'privacy_flare',
  BadMalloc = 'bad_malloc',
  Fd = 'fd_leak',
  MetircKit = 'metrickit_report',
  Anr = 'deadlock',
  VcLeak = 'vc_leak',
  JavaLeak = 'activity_leak',
  Native = 'native_memory',
  Launch = 'launch_metric',
  MemGraph = 'memory_detail_report',
  UserManage = 'user_manage',
  Crash = 'crash',
  ElectronCrash = 'electron_crash',
  Error = 'error',
  Bugly = 'Bugly',
  Symbol = 'symbol',
  ApplicationExit = 'application_exit',
  PowerAnomalyAnalysis='battery_element',
  Split = 'split',
  AndroidFoom='android_foom',
  AndroidANR='android_anr',
  LaunchROM='launch',
  AiMonitor='ai_monitor',
  BindingStatus = 'binding_status',
}

export type FieldScope = Zone | MetricType;
// 实验ID对应自定义字段的key，oa是a11 商业化是a12;
export const TEST_TAG_KEY = 'a11';
// 系统占用的自定义字段 key（实验ID=a11, 业务下钻=a12, 标签过滤=a13）
export const SystemCustomKey = {
  ShiplyTag: 'a11',
  AbTestTag: 'a12',
  TagsInFilter: 'a13',
} as const;


/**
 * 这里字段太多了，要拆成公共字段和各自异常独有的字段
 */
export enum FieldKey {
  StandardAppVersionMode = 'standardAppVersionMode',
  StandardAppVersionModeCompare = 'standardAppVersionModeCompare',
  MallocType='malloc_type',
  Arguments='arguments',
  URL='url',
  Ret = 'ret',
  OrderStatus = 'order_status',
  ResourceStatus = 'resource_status',
  ResourceId = 'resource_id',
  BindingStatus = 'binding_status',
  StandardInferredLabels = 'standardInferredLabels',
  TrafficSize='standardTrafficSize',
  /* 改版新协议字段 */
  PageName='page_name',
  StandardStack='standardStack',
  Os='os',
  StandardStackCompare = 'standardStackCompare',
  // 性能出错堆栈，
  TranslatedStacks='translated_stacks',
  FeatureShow = 'feature_show',
  ObjectType = 'object_type',
  // OOM类型
  OOMType = 'oom_type',

  StandardArea = 'standardArea',
  ServerArea = 'serverArea',

  StandardTagsInFilter = 'standardTagsInFilter',
  // 卡顿耗时
  StandardTimeCost = 'standardTimeCost',
  StandardLaunchDuration = 'standardLaunchDuration',
  PicOverSize = 'pic_over_size',
  StandardPicOverSize = 'standardPicOverSize',
  StandardTimeCostCompare = 'standardTimeCostCompare',
  TimeCost = 'time_cost',
  LaunchDuration = 'launch_duration',
  TimeCostAvg = 'cost_time_average',
  // 业务下钻
  StandardAbTestTag = 'standardAbTestTag',
  StandardAbTestTagCompare = 'standardAbTestTagCompare',
  // 实验ID（shiplyTag）
  StandardShiplyTag = 'standardShiplyAbTestTag',
  StandardShiplyTagCompare = 'standardShiplyAbTestTagCompare',
  // app版本
  StandardAppVersion = 'standardAppVersion',
  StandardAppVersionCompare = 'standardAppVersionCompare',
  // 连续崩溃
  StandardContinuousEvent  = 'standardContinuousEvent',
  // 系统堆栈
  StandardSystemStack = 'standardSystemStack',
  StandardSystemStackCompare = 'standardSystemStackCompare',
  // 监控线程
  StandardMonitoredThreadName = 'standardMonitoredThreadName',
  StandardMonitoredThreadNameCompare = 'standardMonitoredThreadNameCompare',
  StandardBundleId = 'standardBundleId',
  StandardChannelId = 'standardChannelId',
  StandardBundleIdCompare = 'standardBundleIdCompare',
  StandardChannelIdCompare = 'standardChannelIdCompare',
  // 问题特征
  StandardFuncName = 'standardFuncName',
  StandardFuncNameCompare = 'standardFuncNameCompare',
  // issueId
  StandardFeature = 'standardFeature',
  StandardFeatureCompare = 'standardFeatureCompare',
  // 消息ID
  StandardClientIdentify = 'standardClientIdentify',
  StandardClientIdentifyCompare = 'standardClientIdentifyCompare',
  // 进程启动ID
  StandardProcessLaunchId = 'standardProcessLaunchId',
  StandardProcessLaunchIdCompare = 'standardProcessLaunchIdCompare',
  // 启动ID
  StandardLaunchId = 'standardLaunchId',
  StandardLaunchIdCompare = 'standardLaunchIdCompare',
  // 设备ID
  StandardQimei = 'standardQimei',
  StandardQimeiCompare = 'standardQimeiCompare',
  // 用户ID
  StandardAccountId = 'standardAccountId',
  // 命令行
  StandardArguments = 'standardArguments',
  StandardAccountIdCompare = 'standardAccountIdCompare',
  // 平台
  StandardPlatform = 'standardPlatform',
  StandardPlatformCompare = 'standardPlatformCompare',
  // 构建号
  StandardBuildNumber = 'standardBuildNumber',
  StandardBuildNumberCompare = 'standardBuildNumberCompare',
  // Rom
  StandardRom = 'standardRomName',
  StandardRomCompare = 'standardRomNameCompare',
  // 机型
  StandardModel = 'standardModel',
  // 异常现场
  StandardSceneDataLabels = 'standardSceneDataLabels',
  StandardModelCompare = 'standardModelCompare',
  // 系统版本
  StandardOsVersion = 'standardOsVersion',
  StandardOsVersionCompare = 'standardOsVersionCompare',
  // 系统版本
  StandardAsanType = 'standardAsanType',
  StandardAsanTypeCompare = 'standardAsanTypeCompare',
  // 进程名
  StandardProcessName = 'standardProcessName',
  StandardProcessNameCompare = 'standardProcessNameCompare',
  // 场景
  StandardScene = 'standardScene',
  StandardSceneCompare = 'standardSceneCompare',
  // sdk版本
  StandardSdkVersion = 'standardSdkVersion',
  StandardSdkVersionCompare = 'standardSdkVersionCompare',
  // 厂商
  StandardBrand = 'standardBrand',
  StandardBrandCompare = 'standardBrandCompare',

  // 前/后台
  StandardGround = 'standard_ground',
  StandardGroundCompare = 'standard_ground_compare',

  // 版本类型
  StandardVersionMode = 'standard_version_mode',
  StandardVersionModeCompare = 'standard_version_mode_compare',
  StandardFileName = 'standardFileName', // 待分析附件
  // 监控粒度
  StandardDetectStrategy = 'standardDetectStrategy',
  StandardDetectStrategyCompare = 'standardDetectStrategyCompare',

  StandardLlmModel = 'standardModelName',
  StandardNetworkType = 'standardNetworkType',
  StandardSpanName = 'standardSpanName',
  // 改版新协议字段 end
  Stage = 'stage',
  FuncName = 'func_name',
  Feature = 'feature',
  ClientIdentify = 'client_identify',
  AccountId = 'account_id',
  Qimei = 'qimei',
  TagsInFilter='tagsInFilter',
  TagsInFilterUnderline='tags_in_filter',
  ShiplyAbTestTag = 'shiply_ab_test_tag',
  ChannelId = 'channel_id',
  AppInBack = 'app_in_back',
  AbTestTag = 'abTestTag',
  BuildNumber = 'build_number',
  AbTestTagCompare = 'abTestTagCompare',
  ShiplyTag = 'shiplyTag',
  TestTag = 'shiply_tag',
  AbTestTagLine = 'ab_test_tag',
  Area = 'area',
  // 运营商
  Operator = 'operator',
  // 流量异常类型
  ExceptionType = 'exceptionType',
  ParentTrafficSize = 'parentTrafficSize',
  StandardTrafficSizeType='standardTrafficSizeType',
  tcpType = 'tcpType',
  httpType = 'httpType',
  udpType = 'udpType',
  unknownType = 'unknownType',
  MinTrafficSize = 'minTrafficSize',
  MaxTrafficSize = 'maxTrafficSize',
  // 统计方式
  CollectType = 'collectType',
  TrafficType = 'trafficType',
  DeviceType = 'device_type',
  UsedMem = 'usedMem',
  UsedMemCompare = 'usedMemCompare',
  QimeiCount = 'qimeiCount',
  UserCount = 'userCount',
  QimeiCountCompare='qimeiCountCompare',
  DocCount = 'docCount',
  DocCountCompare = 'docCountCompare',
  CustomDate = 'custom_date',
  TimeRange = 'time_range',
  AppVersionOperator = 'app_version_operator',
  StandardAppVersionOperator='standard_app_version_operator',
  AppVersion = 'app_version',
  AppVersionMode = 'app_version_mode',
  OsVersion = 'os_version',
  AsanType='asan',
  Model = 'model',
  Hardware = 'hardware',
  Scene = 'stage',
  DetectStrategy ='detect_strategy',
  Rom = 'rom_name',
  Domain = 'domain',
  Brand = 'brand',
  LaunchType = 'launch_type',
  Span = 'span_name',
  Platform = 'platform',
  ElectronPlatform = 'electron-platform',
  TapdStatus = 'tapd_status',
  TapdHandler = 'tapd_handler',
  IssueStatus = 'issue_status',
  ProcessName = 'process_name',
  AttributionSdk = 'attribution_sdk',
  TagFilter = 'tag_filter',
  Tags = 'tags',
  TagColor = 'tagColor',
  LaunchTagFilter = 'launch_tag_filter',
  LaunchTag = 'launch_tags',
  PageLaunchTag = 'page_launch_tags',
  Bit = 'is64bit',
  VssStage='vss_stage',
  PssStage='pss_stage',
  JavaStage='java_stage',
  fgMemStage='fg_mem_stage',
  ActivityName = 'activity_name',
  PrivacyFunc = 'privacy_func',
  PrivacyType = 'privacy_type',
  FilterSceneProblem = 'filter_scene_problem',
  ProblemType = 'problem_type',
  RiskType = 'risk_type',
  Ground = 'ground',
  Stats = 'stats',
  CoveredStatus = 'cover_status',
  ShowType = 'show_type',
  SdkVersion = 'sdk_version',
  FdType = 'fd_type',
  MetricType = 'metric_type',
  Count = 'count',
  IssueLinkedStatus = 'issue_linked_status',
  FuzzyMatch = 'key_method',
  TopNum = 'topNum',
  LeakBytesMax = 'leakBytesMax',
  BackTime = 'back_time',
  BackTimeFilter = 'back_time_filter',
  BackTimeFilterType = 'back_time_filter_type',
  UserCustom = 'userCustom',
  BizExtendInfo = 'bizExtendInfo',
  ComplianceFilter = 'compliance_filter',
  Stack = 'stack',
  SystemStack = 'system_stack',
  ContinuousEvent = 'continuous_event',
  ShowNew = 'showNew',
  MallocSize = 'mallocSize',
  MallocSizeCompare = 'mallocSizeCompare',
  MaxMallocSize = 'maxMallocSize',
  MaxMallocSizeCompare = 'maxMallocSizeCompare',
  AllMallocSize = 'all_malloc_size',
  P90MallocSize = 'p90MallocSize',
  P90MallocSizeCompare = 'p90MallocSizeCompare',
  P50MallocSize = 'p50MallocSize',
  P50MallocSizeCompare = 'p50MallocSizeCompare',
  P90MaxMallocSize = 'p90MaxMallocSize',
  P90MaxMallocSizeCompare= 'p90MaxMallocSizeCompare',
  P50MaxMallocSize = 'p50MaxMallocSize',
  P50MaxMallocSizeCompare = 'p50MaxMallocSizeCompare',
  AvgMaxMallocSize = 'avgMaxMallocSize',
  AvgMaxMallocSizeCompare = 'avgMaxMallocSizeCompare',
  AvgMallocSize = 'avgMallocSize',
  AvgMallocSizeCompare = 'avgMallocSizeCompare',
  SixElementType = 'six_element_type',
  AppVersionCompare = 'app_version_compare',
  OsVersionCompare = 'os_version_compare',
  SdkVersionCompare = 'sdk_version_compare',
  UserCustomCompare = 'userCustomCompare',
  BizExtendInfoCompare = 'bizExtendInfoCompare',
  TranslatedStatus = 'translated_status',
  BundleId = 'bundle_id',
  OriginalStacks='original_stacks',
  IsInferenceFeature = 'is_inference_feature',
  IsInferenceFeatureCompare = 'is_inference_feature_compare',
  MonitoredThreadName = 'monitored_thread_name',
  CpuArch = 'cpu_arch',
  CpuArchitecture = 'cpu_architecture',
  NodeVersion = 'node_version',
  FrameworkVersion = 'framework_version',
  ElectronCpuArch = 'arch',
  ProcessType = 'process_type',
  // 时间级联
  TimeRangeCascade = 'time_range_cascade',
  TimeWindow = 'time_window',
  TimeQuick = 'time_quick',
  TimeType = 'time_type',
  AccurateDate = 'accurate_date',
  // 扩展版本
  MinAppVersion = 'min_app_version',
  MaxAppVersion = 'max_app_version',
  WildcardAppVersion = 'wildcard_app_version',
  // 附件
  StandardAttachFileNames = 'standardAttachFileNames',
  AttachFileNames = 'attachFileNames',
  AndroidAttachFileNames = 'android_attach_file_names',
  IOSAttachFileNames='ios_attach_file_names',

  LaunchId = 'launch_id',
  ProcessLaunchId = 'process_launch_id',
  IsParticiple = 'is_participle',
  DetailMode = 'detail_mode',
  OffDetailMode = 'off_detail_mode',
  ExitReason = 'exit_reason',
  ExitDescription = 'exit_description',
  FilePath = 'file_path',
  Importance= 'importance',
  CpuArc = 'cpu_arc',
  FrontState = 'frontState',
  NetType='netType',
  ReportType='reportType',
  SmModule = 'sm_module',
  SmPermission = 'sm_permission',
  SmScene = 'sm_scene',
  SmControl = 'sm_control',
  LaunchCost='launchCost',
  SpanCostTime='spanCostTime',
  Linkages='linkages',
  FdCount='fdCount',
  PeerName = 'peerName',
  PowerExceptionType = 'exceptionType',
  Scenes = 'scenes',
  ExceptionFilter='exceptionFilter',
  ExceptionTag='exceptionTag',
  // 疑似FD数量
  SuspiciousCount = 'suspiciousCount',
  HostType = 'host_type',

  // 聚类堆栈
  ClusterStackType='clusterStackType',
  ViewTag = 'view_tag',

  Api = 'api',
  Perm = 'perm',
  Mime = 'mime',
  LlmModel='model_name',
  // crashDiagnostics 字段
  Signal = 'signal',
  TerminationReason = 'termination_reason',
}

export const FIELD_LABEL = {
  [FieldKey.PageName]: i18n.t('页面名称'),
  [FieldKey.AbTestTag]: i18n.t('业务下钻'),
  [FieldKey.BuildNumber]: i18n.t('构建号'),
  [FieldKey.OOMType]: i18n.t('OOM类型'),
  [FieldKey.TagsInFilter]: i18n.t('个例标签'),
  [FieldKey.ShiplyTag]: i18n.t('实验ID'),
  [FieldKey.ReportType]: i18n.t('采集周期'),
  [FieldKey.Domain]: i18n.t('域名'),
  [FieldKey.ChannelId]: i18n.t('渠道'),
  [FieldKey.NetType]: i18n.t('网络类型'),
  [FieldKey.FrontState]: i18n.t('前后台'),
  [FieldKey.Area]: i18n.t('地区'),
  [FieldKey.Operator]: i18n.t('运营商'),
  [FieldKey.ExceptionType]: i18n.t('异常类型'),
  [FieldKey.CollectType]: i18n.t('统计方式'),
  [FieldKey.Brand]: i18n.t('厂商'),
  [FieldKey.TimeRange]: i18n.t('时间范围'),
  [FieldKey.AppVersion]: i18n.t('APP版本'),
  [FieldKey.AppVersionOperator]: i18n.t('APP版本'),
  [FieldKey.AppVersionMode]: i18n.t('构建类型'),
  [FieldKey.OsVersion]: i18n.t('系统版本'),
  [FieldKey.AsanType]: i18n.t('asan类型'),
  [FieldKey.Model]: i18n.t('机型'),
  [FieldKey.StandardBuildNumber]: i18n.t('构建号'),
  [FieldKey.Scene]: i18n.t('场景'),
  [FieldKey.LaunchType]: i18n.t('启动类型'),
  [FieldKey.Span]: i18n.t('Span名'),
  [FieldKey.Rom]: i18n.t('ROM'),
  [FieldKey.Platform]: i18n.t('平台'),
  [FieldKey.TapdStatus]: i18n.t('提单状态'),
  [FieldKey.TapdHandler]: i18n.t('处理人'),
  [FieldKey.IssueStatus]: i18n.t('处理状态'),
  [FieldKey.ProcessName]: i18n.t('进程'),
  [FieldKey.Tags]: i18n.t('问题标签'),
  [FieldKey.TagColor]: i18n.t('标签颜色'),
  [FieldKey.LaunchTag]: i18n.t('自定义tag'),
  [FieldKey.PageLaunchTag]: i18n.t('自定义tag'),
  [FieldKey.Bit]: i18n.t('32/64位'),
  [FieldKey.ActivityName]: i18n.t('页面'),
  [FieldKey.PrivacyFunc]: i18n.t('敏感函数'),
  [FieldKey.FilterSceneProblem]: i18n.t('过滤场景问题'),
  [FieldKey.PrivacyType]: i18n.t('敏感函数分类'),
  [FieldKey.ProblemType]: i18n.t('疑点类型'),
  [FieldKey.Ground]: i18n.t('前/后台'),
  [FieldKey.Stats]: i18n.t('分位'),
  [FieldKey.CoveredStatus]: i18n.t('兜底状态'),
  [FieldKey.ShowType]: i18n.t('视图类型'),
  [FieldKey.SdkVersion]: i18n.t('SDK版本'),
  [FieldKey.FdType]: i18n.t('FD类型'),
  [FieldKey.MetricType]: i18n.t('数据类型'),
  [FieldKey.Count]: i18n.t('上报量'),
  [FieldKey.TopNum]: i18n.t('Top问题数'),
  [FieldKey.ViewTag]: i18n.t('图片信息'),
  [FieldKey.LeakBytesMax]: i18n.t('最大泄漏内存'),
  [FieldKey.BackTime]: i18n.t('非前台时长'),
  [FieldKey.SystemStack]: i18n.t('系统堆栈'),
  [FieldKey.SixElementType]: i18n.t('问题诊断'),
  [FieldKey.TranslatedStatus]: i18n.t('翻译状态'),
  [FieldKey.BundleId]: i18n.t('Bundle ID'),
  [FieldKey.MonitoredThreadName]: i18n.t('监控线程'),
  [FieldKey.AttachFileNames]: i18n.t('附件'),
  [FieldKey.LaunchId]: getTips(i18n.t('启动ID'), launchIdText),
  [FieldKey.ProcessLaunchId]: getTips(i18n.t('进程启动ID'), processLaunchIdText),
  [FieldKey.Stack]: i18n.t('出错堆栈'),
  [FieldKey.ExitReason]: i18n.t('退出原因'),
  [FieldKey.FilePath]: i18n.t('附件'),
  [FieldKey.SmModule]: i18n.t('程序场景名'),
  [FieldKey.SmPermission]: i18n.t('程序权限名'),
  [FieldKey.SmScene]: i18n.t('调用时机'),
  [FieldKey.SmControl]: i18n.t('管控情况'),
  [FieldKey.Api]: i18n.t('调用API'),
  [FieldKey.Mime]: i18n.t('文件类型'),
  [FieldKey.Importance]: i18n.t('前后台'),
};

export enum LaunchType {
  DefaultCold = 'default_cold_launch',
  Cold = 'cold_launch',
  Warm = 'warm_launch',
  ColdPrewarm = 'prewarm_launch'
}

export enum TapdStatus {
  New = 'new',
  InProgress = 'in_progress',
  Assigned = 'assigned',
  Resolved = 'resolved',
  Verified = 'verified',
  Reopened = 'reopened',
  Rejected = 'rejected',
  Closed = 'closed',
  Unassigned = 'unassigned',
  Unconfirmed = 'unconfirmed',
  Planning = 'planning',
  Feedback = 'feedback',
  Acknowledged = 'acknowledged',
  Suspended = 'suspended',
  Postponed = 'postponed',
  TMAudited = 'TM_audited',
  PMMAudited = 'PMM_audited',
  PMAudited = 'PM_audited',
  QAAudited = 'QA_audited',
  Handled = 'handled',
}

export enum Ground {
  Unknown = 'unknown',
  Foreground = 'foreground',
  Background = 'background',
}

export enum AppVersionMode {
  Unknown = 'Unknown',
  Debug = 'Debug',
  Gray = 'Gray',
  Release = 'Release',
}

export enum IssueStatus {
  NotRepaired = 'notRepaired',
  Repairing = 'repairing',
  Recovered = 'recovered',
  Ignore = 'ignore',
  Rejected = 'rejected',
  Suspended = 'suspended',
}

export enum ExportKind {
  IssueDetail = '个例摘要',
}


export enum TapdLinkedState {
  // 未处理
  NotRepaired = 'notRepaired',
  // 已处理
  Recovered = 'recovered',
  // 处理中
  Repairing = 'repairing',
  // 拒绝
  Rejected = 'rejected',
  // 挂起
  Suspended = 'suspended',
  // 暂时忽略，待废弃
  Ignore = 'ignore',
}

export enum Stats {
  Avg = 'avg',
  P25 = 'P25',
  P50 = 'P50',
  P75 = 'P75',
  P90 = 'P90',
  P95 = 'P95',
  P99 = 'P99',
  P50Index = 'p_50',
  P75Index = 'p_75',
  P90Index = 'p_90',
  P95Index = 'p_95',
  P99Index = 'p_99',
}

export enum Platform {
  Android = 'Android',
  iOS = 'iPhone',
  Harmony = 'Harmony'
}

export enum Bit {
  Bit32 = '32bit',
  Bit64 = '64bit',
}

export enum TagFilter {
  Or = 'OR',
  And = 'AND',
  Not = 'NOT',
}

export enum VersionFilter {
  Or = 'OR',        // 包含
  Not = 'NOTMATCH', // 不包含
  Ge = 'GE',        // 大于等于
  Le = 'LE',        // 小于等于
}

export enum AppVersionOperator {
  Or = 'OR',           // 包含
  Ge = 'GE',              // 大于等于
  Le = 'LE',              // 小于等于
  Gt = 'GT',              // 大于
  Lt = 'LT',              // 小于
  Between = 'BETWEEN',    // 范围
  Wildcard = 'WILDCARD',  // 通配符
  NOT='NOT' // 不包含
}

export enum DetailOperator {
  On = 'On',     // 分词
  Off = 'Off',  // 不分词
}
export enum DetailModeOperator{
  MatchPhraseJoin='matchPhrase join',
  MatchPhraseUnion='matchPhrase union',
  FuzzyNatch='fuzzyNatch'
}
export enum Stack{
  EXACT='EXACT',
  MATCH='MATCH'
}

export enum ComplianceFilter {
  Or = 'OR',         // 包含, 合规敏感函数等配置项是全匹配
  Not = 'NOTMATCH',  // 不包含
}

export enum BackTimeFilter {
  Ge = 'GE',     // 大于等于
  Le = 'LE',     // 小于等于
}

export enum TranslateStatus {
  None = '0',
  Whole = '1',
  Partial = '2',
}

export const BACKTIME_FILTER_OPTION = [
  { value: BackTimeFilter.Ge, label: i18n.t('大于') },
];

export const COMPLIANCE_FILTER_OPTION = [
  { value: ComplianceFilter.Or, label: i18n.t('包含') },
  { value: ComplianceFilter.Not, label: i18n.t('不包含') },
];

export const VERSION_FILTER_OPTION = [
  { value: AppVersionOperator.Or, label: i18n.t('包含') },
  { value: AppVersionOperator.Ge, label: i18n.t('大于等于') },
  { value: AppVersionOperator.Gt, label: i18n.t('大于') },
  { value: AppVersionOperator.Le, label: i18n.t('小于等于') },
  { value: AppVersionOperator.Lt, label: i18n.t('小于') },
  { value: AppVersionOperator.Between, label: i18n.t('范围') },
  { value: 'PREFIX', label: i18n.t('开头匹配') },
  { value: 'SUFFIX', label: i18n.t('结尾匹配') },
];

export const SHOWNEW_OPTION = [
  { value: true, label: i18n.t('是') },
  { value: false, label: i18n.t('否') },
];

export const AUTO_CALL_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: '0', label: 'Service#OnCreate' },
  { value: '1', label: 'Service#OnBind' },
  { value: '2', label: 'Service#OnStart' },
  { value: '3', label: 'Receiver#OnReceive' },
  { value: '4', label: 'Provider#OnCreate' },
  { value: '5', label: 'Provider#Insert' },
  { value: '6', label: 'Provider#Delete' },
  { value: '7', label: 'Provider#Update' },
  { value: '8', label: 'Provider#Query' },
  { value: '9', label: 'Provider#GetType' },
  { value: '10', label: i18n.t('Receiver关联启动') },
  { value: '11', label: i18n.t('Service关联启动') },
  { value: '12', label: i18n.t('Provider关联启动') },
];

export const AUTO_CALL_SELF_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: '0', label: i18n.t('自启动问题') },
  { value: '1', label: i18n.t('疑似自启动问题') },
  { value: '2', label: i18n.t('关联启动问题') },
  { value: '3', label: i18n.t('疑似关联启动问题') },
];

export const ISSUE_STATUS_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: IssueStatus.NotRepaired, label: i18n.t('未处理') },
  { value: IssueStatus.Repairing, label: i18n.t('处理中') },
  { value: IssueStatus.Recovered, label: i18n.t('已处理') },
  { value: IssueStatus.Ignore, label: i18n.t('忽略') },
];

export enum IssueStatusCompliance {
  Solved = 'solved',
  Disclosed = 'disclosed',
  Delay = 'delay',
  NoEffect = 'noEffect',
  Hang = 'hang',
  Mistake = 'mistake',
  Reopen = 'reopen',
  Dispute = 'dispute',
  WaitSolved = 'waitSolved',
  NotRepaired = 'notRepaired',
  Repairing = 'repairing',
  InThirdSdk = 'inThirdSdk',
  OutThirdSdk = 'outThirdSdk',
}

export const ISSUE_STATUS_COMPLIANCE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: IssueStatusCompliance.Solved, label: i18n.t('已解决') },
  { value: IssueStatusCompliance.Disclosed, label: i18n.t('已披露') },
  { value: IssueStatusCompliance.Delay, label: i18n.t('已报备延期') },
  { value: IssueStatusCompliance.Reopen, label: i18n.t('重新打开') },
  { value: IssueStatusCompliance.NotRepaired, label: i18n.t('待评估') },
];

export const ISSUE_STATUS_COMPLIANCE_DEPRECATED_OPTION = [
  { value: IssueStatusCompliance.NoEffect, label: i18n.t('已处置') },
  { value: IssueStatusCompliance.Hang, label: i18n.t('已处置') },
  { value: IssueStatusCompliance.Mistake, label: i18n.t('已处置') },
  { value: IssueStatusCompliance.Dispute, label: i18n.t('处理中') },
  { value: IssueStatusCompliance.WaitSolved, label: i18n.t('处理中') },
];

export const ISSUE_STATUS_COMPLIANCE_LITE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: IssueStatusCompliance.NotRepaired, label: i18n.t('未处置') },
  { value: IssueStatusCompliance.Repairing, label: i18n.t('处置中') },
  { value: IssueStatusCompliance.Solved, label: i18n.t('已处置') },
  { value: IssueStatusCompliance.Disclosed, label: i18n.t('已披露') },
  { value: IssueStatusCompliance.Mistake, label: i18n.t('误报，拒绝') },
];

export const ISSUE_STATUS_COMPLIANCE_NETWORK_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: IssueStatusCompliance.NotRepaired, label: i18n.t('未处置') },
  { value: IssueStatusCompliance.Reopen, label: i18n.t('重新打开') },
  { value: IssueStatusCompliance.Repairing, label: i18n.t('处置中') },
  { value: IssueStatusCompliance.Solved, label: i18n.t('已解决') },
  { value: IssueStatusCompliance.Disclosed, label: i18n.t('已披露') },
  { value: IssueStatusCompliance.Mistake, label: i18n.t('已拒绝（误报）') },
  { value: IssueStatusCompliance.Delay, label: i18n.t('已报备延期') },
  { value: IssueStatusCompliance.InThirdSdk, label: i18n.t('已确认（司内三方SDK）') },
  { value: IssueStatusCompliance.OutThirdSdk, label: i18n.t('已确认（司外三方SDK）') },
];

export const COMPLIANCE_FILE_ACCESS_PERMISSION_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'none', label: i18n.t('无') },
  { value: 'r', label: i18n.t('读') },
  { value: 'w', label: i18n.t('写') },
  { value: 'rw', label: i18n.t('读写') },
];

export const STACK_INFO_COMPLIANCE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 1, label: i18n.t('有堆栈') },
  { value: 2, label: i18n.t('无堆栈') },
];


export const EXIT_REASON_OPTION = [
  { value: '6', label: 'REASON_ANR' },
  { value: '4', label: 'REASON_CRASH' },
  { value: '5', label: 'REASON_CRASH_NATIVE' },
  { value: '12', label: 'REASON_DEPENDENCY_DIED' },
  { value: '9', label: 'REASON_EXCESSIVE_RESOURCE_USAGE' },
  { value: '1', label: 'REASON_EXIT_SELF' },
  { value: '14', label: 'REASON_FREEZER' },
  { value: '7', label: 'REASON_INITIALIZATION_FAILURE' },
  { value: '3', label: 'REASON_LOW_MEMORY' },
  { value: '13', label: 'REASON_OTHER' },
  { value: '8', label: 'REASON_PERMISSION_CHANGE' },
  { value: '2', label: 'REASON_SIGNALED' },
  { value: '0', label: 'REASON_UNKNOWN' },
  { value: '10', label: 'REASON_USER_REQUESTED' },
  { value: '11', label: 'REASON_USER_STOPPED' },
];

export const IOS_EXIT_REASON_OPTION = [
  { value: '0', label: i18n.t('其他') },
  { value: '16', label: i18n.t('用户退出') },
  { value: '17', label: i18n.t('Crash(可捕获)') },
  { value: '18', label: i18n.t('设备重启/升级') },
  { value: '19', label: i18n.t('设备重启/升级') },
  { value: '20', label: 'ANR' },
  { value: '21', label: 'ANR' },
  { value: '22', label: i18n.t('启动超时') },
  { value: '23', label: i18n.t('启动超时') },
  { value: '24', label: i18n.t('后台被杀') },
  { value: '25', label: 'FOOM' },
  { value: '26', label: i18n.t('键盘启动超时') },
];

export const IOS_EXIT_VALUE = [
  { value: ['17'], key: 'Crash' },
  { value: ['20', '21'], key: 'ANR' },
  { value: ['25'], key: 'FOOM' },
  { value: ['22', '23'], key: 'launchDeadlock' },
  { value: ['26'], key: 'kbLaunchQuit' },
  { value: ['0'], key: 'unknown' },
  { value: ['16'], key: 'terminate' },
  { value: ['18', '19'], key: 'deviceReboot' },
  { value: ['24'], key: 'boom' },
];

export const VERSION_MODE_OPTIONS = [
  { value: AppVersionMode.Unknown, label: i18n.t('未知') },
  { value: AppVersionMode.Debug, label: i18n.t('开发版') },
  { value: AppVersionMode.Gray, label: i18n.t('灰度版') },
  { value: AppVersionMode.Release, label: i18n.t('正式版') },
];

export const IOS_EXIT_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'Crash', label: i18n.t('Crash(可捕获)') },
  { value: 'ANR', label: 'ANR' },
  { value: 'FOOM', label: 'FOOM' },
  { value: 'launchDeadlock', label: i18n.t('启动超时') },
  { value: 'kbLaunchQuit', label: i18n.t('键盘启动超时') },
  { value: 'unknown', label: i18n.t('其他') },
  { value: 'terminate', label: i18n.t('用户退出') },
  { value: 'deviceReboot', label: i18n.t('设备重启/升级') },
  { value: 'boom', label: i18n.t('后台被杀') },
];

export const COLLECT_DATA_IOS_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'IDFA', label: 'IDFA' },
  { value: 'IDFV', label: 'IDFV' },
  { value: 'MacAddress', label: 'MacAddress' },
  { value: 'MachineModel', label: 'MachineModel' },
  { value: 'DeviceModel', label: 'DeviceModel' },
  { value: 'Phone', label: 'Phone' },
  { value: 'IDCard', label: 'IDCard' },
  { value: 'BankCard', label: 'BankCard' },
  { value: 'HomeAddress', label: 'HomeAddress' },
  { value: 'Email', label: 'Email' },
  { value: 'Name', label: 'Name' },
  { value: 'Location', label: 'Location' },
  { value: 'Password', label: 'Password' },
  { value: 'Birthday', label: 'Birthday' },
  { value: 'Gender', label: 'Gender' },
  { value: 'WifiSSID', label: 'WifiSSID' },
  { value: 'WifiBSSID', label: 'WifiBSSID' },
  { value: 'CarrierName', label: 'CarrierName' },
  { value: 'UserName', label: 'UserName' },
  { value: 'Pasteboard', label: 'Pasteboard' },
];

export const COLLECT_DATA_ANDROID_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'AndroidID', label: 'AndroidID' },
  { value: 'AppBroadcast', label: 'AppBroadcast' },
  { value: 'AppInstall', label: 'AppInstall' },
  { value: 'SN', label: 'SN' },
  { value: 'IMEI', label: 'IMEI' },
  { value: 'IMSI', label: 'IMSI' },
  { value: 'MacAddress', label: 'MacAddress' },
  { value: 'DeviceModel', label: 'DeviceModel' },
  { value: 'IDCard', label: 'IDCard' },
  { value: 'CellID', label: 'CellID' },
  { value: 'BankCard', label: 'BankCard' },
  { value: 'Phone', label: 'Phone' },
  { value: 'HomeAddress', label: 'HomeAddress' },
  { value: 'Email', label: 'Email' },
  { value: 'Name', label: 'Name' },
  { value: 'Location', label: 'Location' },
  { value: 'Gender', label: 'Gender' },
  { value: 'Password', label: 'Password' },
  { value: 'Birthday', label: 'Birthday' },
  { value: 'SSID', label: 'SSID' },
  { value: 'BSSID', label: 'BSSID' },
  { value: 'Clipboard', label: 'Clipboard' },
  { value: 'OAID', label: 'OAID' },
  { value: 'NearWifiMac', label: 'NearWifiMac' },
  { value: 'NearWifiSsid', label: 'NearWifiSsid' },
  { value: 'NearMcc', label: 'NearMcc' },
  { value: 'NearMnc', label: 'NearMnc' },
  { value: 'NearCid', label: 'NearCid' },
  { value: 'NearLac', label: 'NearLac' },
];

export const REQUEST_SOURCE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'HTTP', label: 'HTTP' },
  { value: 'HTTPS', label: 'HTTPS' },
  { value: 'PB', label: 'PB' },
  { value: 'JCE', label: 'JCE' },
  { value: 'Socket', label: 'Socket' },
];

export const HOST_TYPE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'domain', label: 'URL' },
  { value: 'ipv4', label: 'IPV4' },
  { value: 'ipv6', label: 'IPV6' },
];

export const FORE_BACK_GROUND_OPTION = [
  { value: 2, label: i18n.t('所有') },
  { value: 0, label: i18n.t('前台') },
  { value: 1, label: i18n.t('后台') },
];

export const SM_SCENE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'before', label: i18n.t('隐私协议同意前调用') },
  { value: 'background', label: i18n.t('非前台调用') },
  { value: 'foreground', label: i18n.t('前台调用') },
];

export const SM_CONTROL_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: 'ban', label: i18n.t('拦截调用') },
  { value: 'normal', label: i18n.t('正常调用') },
];

export enum ProblemType {
  IllegalScene = 'illegal_scene',
  Before = 'before',
  Back = 'back',
  HighFreq = 'high_freq',
  Silence = 'silence',
  DenyRetry = 'deny_retry',
  Normal = 'normal',
  OverCall = 'over_call',
  ModNoPerm = 'mod_no_perm',
  AgreeBeforeTrans = 'agree_before_trans',
  PlainTextTrans = 'plain_text_trans',
  OverseasTrans = 'overseas_trans',
  BackgroundTrans = 'background_trans',
  OverclockTrans = 'overclock_trans',
  ShareTrans = 'share_trans',
  FrontTrans = 'front_trans',
  HttpInsecureTrans = 'http_insecure_trans',
  StartupPhaseTrans = 'startup_phase_trans',
  AgreeBefore = 'agree_before',
  NoPermNoMedia = 'no_perm_no_media',
  HasPermNoMedia = 'has_perm_no_media',
  BootMedia = 'boot_media',
  NoBootNoPermMedia = 'no_boot_no_perm_media',
  NoBootHasPermMedia = 'no_boot_has_perm_media',
  NoPermFile = 'no_perm_file',
}

export const PROBLEM_TYPE_ISSUE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: ProblemType.IllegalScene, label: i18n.t('页面不允许调用') },
  { value: ProblemType.Before, label: i18n.t('隐私协议同意前调用') },
  { value: ProblemType.Back, label: i18n.t('非前台调用') },
  { value: ProblemType.HighFreq, label: i18n.t('高频调用') },
  { value: ProblemType.Silence, label: i18n.t('前台静默调用') },
  { value: ProblemType.DenyRetry, label: i18n.t('未授权调用') },
  { value: ProblemType.Normal, label: i18n.t('关注函数前台调用') },
  { value: ProblemType.OverCall, label: i18n.t('关注函数超频调用') },
  { value: ProblemType.ModNoPerm, label: i18n.t('模块无权限调用') },
];

export const PROBLEM_TYPE_FLARE_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: ProblemType.Before, label: i18n.t('隐私协议同意前调用') },
  { value: ProblemType.Back, label: i18n.t('非前台调用') },
  { value: ProblemType.Silence, label: i18n.t('前台静默调用') },
];

export const PROBLEM_TYPE_ISSUE_HARMONY_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: ProblemType.Before, label: i18n.t('隐私协议同意前调用') },
  { value: ProblemType.Back, label: i18n.t('非前台调用') },
];

export const PROBLEM_TYPE_NETWORK_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: ProblemType.HttpInsecureTrans, label: i18n.t('HTTP防劫持专项') },
  { value: ProblemType.AgreeBeforeTrans, label: i18n.t('隐私协议同意前传输') },
  { value: ProblemType.PlainTextTrans, label: i18n.t('明文传输') },
  { value: ProblemType.OverseasTrans, label: i18n.t('境外共享') },
  { value: ProblemType.BackgroundTrans, label: i18n.t('非前台传输') },
  { value: ProblemType.OverclockTrans, label: i18n.t('超频传输') },
  { value: ProblemType.ShareTrans, label: i18n.t('对外共享') },
  { value: ProblemType.FrontTrans, label: i18n.t('前台传输') },
  { value: ProblemType.StartupPhaseTrans, label: i18n.t('启动阶段传输') },
];

export const PROBLEM_TYPE_FILE_ACCESS_OPTION = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: ProblemType.AgreeBefore, label: i18n.t('未同意隐私政策前访问文件') },
  { value: ProblemType.NoPermNoMedia, label: i18n.t('未授予存储权限访问非影音文件') },
  { value: ProblemType.HasPermNoMedia, label: i18n.t('已授予存储权限访问非影音文件') },
  { value: ProblemType.BootMedia, label: i18n.t('启动阶段访问影音文件') },
  { value: ProblemType.NoBootNoPermMedia, label: i18n.t('非启动阶段未授予存储权限访问影音文件') },
  { value: ProblemType.NoBootHasPermMedia, label: i18n.t('非启动阶段已授予存储权限访问影音文件') },
  { value: ProblemType.NoPermFile, label: i18n.t('其他非权限相关调用') },
];

export enum ScanStatus {
  ALL = 0, // 默认全部
  SUC = 1, // 扫描成功
  DOING = 2, // 扫描进行中
  FAIL = 3, // 扫描失败
  TIMEOUT = 4, // 超时
}

export enum NotifyStatus {
  MAIL_NOTIFY_NONE = 0,
  MAIL_NOTIFY_ENABLE = 1,
  MAIL_NOTIFY_DISABLE = 2,
}

export const COMPLIANCE_SCAN_STATUS_OPTION = [
  { value: ScanStatus.ALL, label: i18n.t('所有') },
  { value: ScanStatus.SUC, label: i18n.t('扫描完成') },
  { value: ScanStatus.DOING, label: i18n.t('扫描中') },
  { value: ScanStatus.FAIL, label: i18n.t('扫描失败') },
  { value: ScanStatus.TIMEOUT, label: i18n.t('超时') },
];

export const ANDROID_CRASH_LINKAGES_OPTION = [
  { value: 'memory.fd_leak', label: i18n.t('FD详情') },
  { value: 'memory.activity_leak', label: i18n.t('Java内存泄漏') },
  { value: 'memory.big_bitmap', label: i18n.t('大图分析') },
  { value: 'memory.native_memory', label: i18n.t('Native内存详情') },
  { value: 'memory.java_memory_ceiling_hprof', label: i18n.t('Java内存详情') },
];
export const ANDROID_ANR_LINKAGES_OPTION = [
  { value: 'looper.looper_stack', label: i18n.t('卡顿') },
];
export const IOS_FOOM_LINKAGES_OPTION = [
  { value: 'memory.vc_leak', label: i18n.t('VC内存泄漏') },
  { value: 'memory.bad_malloc', label: i18n.t('大块内存分配') },
  { value: 'memory.vmgraph', label: i18n.t('内存详情') },
];
export const IOS_FOOM_IS_IS_INFERENCE_FEATURE = [
  { value: 'NOT', label: i18n.t('嵌入聚类(嵌入相似有堆栈问题中)') },
  { value: 'OR', label: i18n.t('独立聚类(单独聚类为“无堆栈问题”)') },
];

export enum Memory{
  activityLeak='memory.activity_leak',
  javaMemoryDetail='memory.java_memory_ceiling_hprof',
  fdLeak='memory.fd_leak',
  nativeMemory='memory.native_memory',
  bigBitmap='memory.big_bitmap'
}
export const ANDROID_FOOM_LINKAGES_OPTION = [
  { value: Memory.activityLeak, label: i18n.t('Java内存泄漏') },
  { value: Memory.javaMemoryDetail, label: i18n.t('Java内存详情') },
  { value: Memory.fdLeak, label: i18n.t('FD详情') },
  { value: Memory.nativeMemory, label: i18n.t('Native内存详情') },
  { value: Memory.bigBitmap, label: i18n.t('大图分析') },
];

export const ASAN_TYPE_OPTION = [
  { value: '0', label: 'UNKNOWN' },
  { value: '1', label: 'OVERFLOW' },
  { value: '2', label: 'UNDERFLOW' },
  { value: '3', label: 'USE_AFTER_FREE' },
  { value: '4', label: 'DOUBLE_FREE' },
];

export const getStandardLocalField = (field: string) => {
  const fields: Partial<Record<string, DefaultOptionType[]>> = {
    app_in_back: [
      { label: i18n.t('前台'), value: '3' },
      { label: i18n.t('后台'), value: '2' },
    ],
  };

  return fields[field] || [];
};

export const TAPD_STATUS = [
  { value: FIELD_ALL, label: i18n.t('所有') },
  { value: TapdStatus.New, label: i18n.t('新建') },
  { value: TapdStatus.InProgress, label: i18n.t('接受/处理') },
  { value: TapdStatus.Assigned, label: i18n.t('已分配') },
  { value: TapdStatus.Resolved, label: i18n.t('已解决') },
  { value: TapdStatus.Verified, label: i18n.t('已验证') },
  { value: TapdStatus.Reopened, label: i18n.t('重新打开') },
  { value: TapdStatus.Rejected, label: i18n.t('已拒绝') },
  { value: TapdStatus.Closed, label: i18n.t('已关闭') },
  { value: TapdStatus.Unassigned, label: i18n.t('未派单') },
  { value: TapdStatus.Postponed, label: i18n.t('挂起') },
];

export const IOS_FILE_NAME_OPTIONS = [
  { label: i18n.t('自定义日志'), value: 'log.txt.zip' },
  { label: i18n.t('业务附件'), value: 'crash_attach.log' },
  { label: 'martianlog.txt', value: 'martianlog.txt' },
  { label: 'meminfo.txt', value: 'meminfo.txt' },
  { label: 'user_data.log', value: 'user_data.log' },
  { label: 'valueMapOthres.log', value: 'valueMapOthres.log' },
  { label: 'crash_thread_dispatch_recording.log', value: 'crash_thread_dispatch_recording.log' },
  { label: 'fault_address_info.log', value: 'fault_address_info.log' },
];

export const FILE_NAME_OPTIONS = [
  { label: i18n.t('系统日志'), value: '系统日志' },
  { label: 'jniLog.txt', value: 'jniLog.txt' },
  { label: 'user_datas.log', value: 'user_datas.log' },
  { label: 'valueMapOthers.txt', value: 'valueMapOthers.txt' },
  { label: 'crashInfos.txt', value: 'crashInfos.txt' },
  { label: 'alltimes.txt', value: 'alltimes.txt' },
];

export const getLocalField = (fieldKey: FieldKey) => {
  const fields: Partial<Record<FieldKey, ExOption[]>> = {
    [FieldKey.IsInferenceFeature]: [
      { value: 'NOT', label: i18n.t('嵌入聚类(嵌入相似有堆栈问题中)') },
      { value: 'OR', label: i18n.t('独立聚类(单独聚类为“无堆栈问题”)') }],
    [FieldKey.OriginalStacks]: [
      { value: 'NOT', label: i18n.t('有') },
      { value: 'OR', label: i18n.t('无') }],
    [FieldKey.CollectType]: [
      { value: 'auto', label: i18n.t('通用统计') },
      { value: 'custom', label: i18n.t('业务统计') },
    ],
    [FieldKey.ContinuousEvent]: [
      { label: i18n.t('连续崩溃'), value: '1' },
      { label: i18n.t('非连续崩溃'), value: '0' },
    ],
    [FieldKey.StandardFileName]: [
      { label: i18n.t('系统日志'), value: '系统日志' },
      { label: 'jniLog.txt', value: 'jniLog.txt' },
      { label: 'user_datas.log', value: 'user_datas.log' },
      { label: 'valueMapOthers.txt', value: 'valueMapOthers.txt' },
      { label: 'crashInfos.txt', value: 'crashInfos.txt' },
      { label: 'alltimes.txt', value: 'alltimes.txt' },
    ],
    [FieldKey.Platform]: [
      { label: i18n.t('所有'), value: FIELD_ALL },
      { label: 'Android', value: 'Android' },
      { label: 'iOS', value: 'iPhone' },
    ],
    [FieldKey.ElectronPlatform]: [
      { label: i18n.t('所有'), value: FIELD_ALL },
      { label: 'Windows', value: 'windows' },
      { label: 'Linux', value: 'linux' },
      { label: 'Mac', value: 'macOS' },
    ],
    [FieldKey.AppInBack]: [
      { label: i18n.t('所有'), value: '0' },
      { label: i18n.t('前台'), value: '3' },
      { label: i18n.t('后台'), value: '2' },
      // { label: i18n.t('未知'), value: '1' },
    ],
    [FieldKey.ShowNew]: SHOWNEW_OPTION,
    [FieldKey.LaunchType]: isIOS() || isVisionOS() || isAppleTV() ? [
      { value: LaunchType.Cold, label: i18n.t('冷启动（非 prewarm）') },
      { value: LaunchType.ColdPrewarm, label: i18n.t('冷启动（prewarm）') },
      { value: LaunchType.Warm, label: i18n.t('前后台切换') },
    ] : [
      { value: LaunchType.Cold, label: i18n.t('冷启动') },
      { value: LaunchType.Warm, label: i18n.t('温启动') },
    ],
    [FieldKey.TapdStatus]: TAPD_STATUS,
    [FieldKey.Ground]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: Ground.Unknown, label: i18n.t('未知') },
      { value: Ground.Foreground, label: i18n.t('前台') },
      { value: Ground.Background, label: i18n.t('后台') },
    ],
    [FieldKey.IssueStatus]: ISSUE_STATUS_OPTION,
    [FieldKey.IssueLinkedStatus]: [
      { value: TapdLinkedState.NotRepaired, label: i18n.t('未处理') },
      { value: TapdLinkedState.Repairing, label: i18n.t('处理中') },
      { value: TapdLinkedState.Recovered, label: i18n.t('已处理') },
      { value: TapdLinkedState.Rejected, label: i18n.t('拒绝') },
      { value: TapdLinkedState.Suspended, label: i18n.t('挂起') },
    ],
    [FieldKey.Stats]: [
      { value: Stats.Avg, label: i18n.t('平均值') },
      { value: Stats.P50, label: 'P50' },
      { value: Stats.P75, label: 'P75' },
      { value: Stats.P90, label: 'P90' },
      { value: Stats.P95, label: 'P95' },
      { value: Stats.P99, label: 'P99' },
    ],
    [FieldKey.Bit]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: Bit.Bit32, label: i18n.t('32位') },
      { value: Bit.Bit64, label: i18n.t('64位') },
    ],
    [FieldKey.TagFilter]: [
      { value: TagFilter.Or, label: 'OR' },
      { value: TagFilter.And, label: 'AND' },
      { value: TagFilter.Not, label: 'NOT' },
    ],
    [FieldKey.PowerExceptionType]: [
      { value: TagFilter.Or, label: 'OR' },
      { value: TagFilter.And, label: 'AND' },
    ],
    [FieldKey.LaunchTagFilter]: [
      { value: TagFilter.Or, label: 'OR' },
      { value: TagFilter.And, label: 'AND' },
      { value: TagFilter.Not, label: 'NOT' },
    ],
    [FieldKey.ProblemType]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      ...([
        ...PROBLEM_TYPE_ISSUE_OPTION,
        ...PROBLEM_TYPE_NETWORK_OPTION,
        ...PROBLEM_TYPE_FILE_ACCESS_OPTION,
      ].filter(item => item.value !== FIELD_ALL)),
    ],
    [FieldKey.PrivacyType]: isIOS() || isVisionOS() || isAppleTV() ? [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: 'Location', label: i18n.t('定位（Location）') },
      { value: 'Wi-Fi', label: 'Wi-Fi（Wi-Fi）' },
      { value: 'PhotoLibrary', label: i18n.t('相册（PhotoLibrary）') },
      { value: 'Pasteboard', label: i18n.t('剪贴板（Pasteboard）') },
      { value: 'Contacts', label: i18n.t('通讯录（Contacts）') },
      { value: 'Camera', label: i18n.t('相机（Camera）') },
      { value: 'Microphone', label: i18n.t('麦克风（Microphone）') },
      { value: 'UserTracking', label: i18n.t('用户跟踪（UserTracking）') },
      { value: 'IDFV', label: 'IDFV（IDFV）' },
      { value: 'DeviceInfo', label: i18n.t('设备信息（DeviceInfo）') },
      { value: 'Calendars/Reminders', label: i18n.t('日历与提醒（Calendars/Reminders）') },
      { value: 'Notification', label: i18n.t('通知（Notification）') },
      { value: 'Speech', label: i18n.t('语音识别（Speech）') },
      { value: 'Health', label: i18n.t('健康（Health）') },
      { value: 'HomeKit', label: 'HomeKit（HomeKit）' },
      { value: 'AppleMusic', label: 'AppleMusic（AppleMusic）' },
      { value: 'Motion', label: i18n.t('运动与健身（Motion）') },
      { value: 'Bluetooth', label: i18n.t('蓝牙（Bluetooth）') },
      { value: 'Siri', label: 'Siri（Siri）' },
      { value: 'CellularProviders', label: i18n.t('运营商（CellularProviders）') },
      { value: 'FaceID', label: 'FaceID（FaceID）' },
      { value: 'GKFriendList', label: 'GameKit（GKFriendList）' },
      { value: 'SensorKit', label: i18n.t('传感器（SensorKit）') },
      { value: 'NearbyInteraction', label: i18n.t('近场交互（NearbyInteraction）') },
      { value: 'NFCReader', label: 'NFCReader（NFCReader）' },
    ] : [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: 'device', label: i18n.t('设备信息（device）') },
      { value: 'number', label: i18n.t('手机信息（number）') },
      { value: 'appinfo', label: i18n.t('已安装应用（appinfo）') },
      { value: 'location', label: i18n.t('定位（location）') },
      { value: 'clipboard', label: i18n.t('剪切版（clipboard）') },
      { value: 'camera', label: i18n.t('摄像头（camera）') },
      { value: 'recorder', label: i18n.t('录音（recorder）') },
      { value: 'contact', label: i18n.t('通讯录（contact）') },
      { value: 'photo', label: i18n.t('相册（photo）') },
      { value: 'network', label: i18n.t('网络（network）') },
      { value: 'bluetooth', label: i18n.t('蓝牙（bluetooth）') },
      { value: 'sms', label: i18n.t('短信（sms）') },
      { value: 'calendar', label: i18n.t('日历（calendar）') },
      { value: 'sdcard', label: i18n.t('SD卡（sdcard）') },
      { value: 'sensor', label: i18n.t('传感器（sensor）') },
    ],
    [FieldKey.CoveredStatus]: [
      { value: FIELD_ALL, label: i18n.t('所有'), disabled: false },
      ...stores.RightlyStore.coveredStatusOptions?.map(option => ({
        value: option.key,
        label: option.key,
        disabled: (option.products?.length && !option.products?.includes(getProductId()))
          || (option.platforms?.length && !option.platforms?.includes(getPlatform()?.toLowerCase())),
      })),
    ].filter(item => !item.disabled),
    [FieldKey.ShowType]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: '0', label: 'background' },
      { value: '1', label: 'source' },
    ],
    [FieldKey.FdType]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: 'eventfd', label: i18n.t('eventfd（线程）') },
      { value: 'file', label: i18n.t('file（业务文件）') },
      { value: 'system_file', label: i18n.t('system（系统文件）') },
      { value: 'gpu', label: i18n.t('gpu（GPU文件）') },
      { value: 'dmabuf', label: i18n.t('dmabuf（图形缓冲区）') },
      { value: 'ashmem', label: i18n.t('ashmem（匿名内存）') },
      { value: 'device', label: i18n.t('device（设备文件）') },
      { value: 'socket', label: 'socket（Socket）' },
      { value: 'pipe', label: i18n.t('pipe（管道文件）') },
      { value: 'others', label: i18n.t('others（其他）') },
    ],
    [FieldKey.BackTimeFilter]: BACKTIME_FILTER_OPTION,
    [FieldKey.ComplianceFilter]: COMPLIANCE_FILTER_OPTION,
    [FieldKey.TagColor]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      ...TAG_COLOR_INFO.map(item => ({
        label: getColorLabel(item.color, true),
        value: item.color,
      })),
    ],
    [FieldKey.SystemStack]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: '是', label: i18n.t('是') },
      { value: '否', label: i18n.t('否') },
    ],
    [FieldKey.AppVersionMode]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: AppVersionMode.Unknown, label: i18n.t('未知') },
      { value: AppVersionMode.Debug, label: i18n.t('开发版') },
      { value: AppVersionMode.Gray, label: i18n.t('灰度版') },
      { value: AppVersionMode.Release, label: i18n.t('正式版') },
    ],
    [FieldKey.TranslatedStatus]: [
      { value: FIELD_ALL, label: i18n.t('所有') },
      { value: TranslateStatus.None, label: i18n.t('未翻译') },
      { value: TranslateStatus.Whole, label: i18n.t('完全翻译') },
      { value: TranslateStatus.Partial, label: i18n.t('部分翻译') },
    ],
    [FieldKey.AppVersionOperator]: [
      { value: AppVersionOperator.Or, label: i18n.t('包含') },
      { value: AppVersionOperator.NOT, label: i18n.t('不包含') },
      { value: AppVersionOperator.Ge, label: i18n.t('大于等于') },
      { value: AppVersionOperator.Gt, label: i18n.t('大于') },
      { value: AppVersionOperator.Le, label: i18n.t('小于等于') },
      { value: AppVersionOperator.Lt, label: i18n.t('小于') },
      { value: AppVersionOperator.Between, label: i18n.t('范围') },
      { value: AppVersionOperator.Wildcard, label: i18n.t('通配符') },
    ],
    [FieldKey.StandardAppVersionOperator]: [
      {
        value: MatchMode.OR,
        label: i18n.t('是'),
      },
      {
        value: MatchMode.NOT,
        label: i18n.t('不是'),
      },
      {
        value: MatchMode.Gt,
        label: i18n.t('大于'),
      },
      {
        value: MatchMode.Ge,
        label: i18n.t('大于等于'),
      },
      {
        value: MatchMode.Lt,
        label: i18n.t('小于'),
      },
      {
        value: MatchMode.Le,
        label: i18n.t('小于等于'),
      },
      {
        value: MatchMode.Between,
        label: i18n.t('区间'),
      },
      {
        value: MatchMode.MATCH,
        label: i18n.t('匹配'),
      },
      {
        value: MatchMode.NOTMATCH,
        label: i18n.t('不匹配'),
      },
      {
        value: MatchMode.ISEMPTY,
        label: i18n.t('为空'),
      },
      {
        value: MatchMode.NOTEMPTY,
        label: i18n.t('不为空'),
      },
      {
        value: MatchMode.PREFIX,
        label: i18n.t('开头匹配'),
      },
      {
        value: MatchMode.NOTPREFIX,
        label: i18n.t('开头不匹配'),
      },
      {
        value: MatchMode.SUFFIX,
        label: i18n.t('结尾匹配'),
      },
      {
        value: MatchMode.NOTSUFFIX,
        label: i18n.t('结尾不匹配'),
      },
      {
        value: MatchMode.REGEXP,
        label: i18n.t('正则表达式'),
      },
    ],
    [FieldKey.IOSAttachFileNames]: [
      { value: FIELD_ALL, label: i18n.t('全部') },
      { value: 'meminfo.txt', label: 'meminfo.txt' },
      { value: 'user_datas.log', label: 'user_datas.log' },
      { value: 'valueMapOthers.txt', label: 'valueMapOthers.txt' },
      { value: 'crash_attach.log', label: 'crash_attach.log' },
      { value: 'buglylog.txt', label: 'buglylog.txt' },
      { value: 'martianlog.txt', label: 'martianlog.txt' },
      { value: 'crash.log', label: 'crash.log' },
      { value: 'detail_data.json', label: 'detail_data.json' },
    ],
    [FieldKey.AndroidAttachFileNames]: [
      { value: FIELD_ALL, label: i18n.t('全部') },
      { value: 'tomb.zip', label: 'tomb.zip' },
      { value: 'jniLog.txt', label: 'jniLog.txt' },
      { value: 'user_datas.log', label: 'user_datas.log' },
      { value: 'valueMapOthers.txt', label: 'valueMapOthers.txt' },
      { value: 'crashInfos.txt', label: 'crashInfos.txt' },
      { value: 'alltimes.txt', label: 'alltimes.txt' },
      { value: 'log.txt', label: 'log.txt' },
      { value: 'detail_data.json', label: 'detail_data.json' },
      { value: 'crash.log', label: 'crash.log' },
    ],
    [FieldKey.IsParticiple]: [
      { value: DetailOperator.Off, label: i18n.t('不分词') },
      { value: DetailOperator.On, label: i18n.t('分词') },
    ],
    [FieldKey.DetailMode]: [
      { value: DetailModeOperator.FuzzyNatch, label: i18n.t('模糊匹配') },
      { value: DetailModeOperator.MatchPhraseUnion, label: i18n.t('并集') },
      { value: DetailModeOperator.MatchPhraseJoin, label: i18n.t('交集') },
    ],
    [FieldKey.OffDetailMode]: [
      { value: 'matchPhrase union', label: 'OR' },
      /* { value: 'matchPhrase join', label: i18n.t('交集') },*/
    ],
    [FieldKey.FilePath]: [
      { value: FIELD_ALL, label: i18n.t('不限') },
      { value: 'NOT', label: i18n.t('不包含') },
      { value: 'OR', label: i18n.t('包含') },
    ],
    [FieldKey.Signal]: [
      { value: '11', label: 'EXC_BAD_ACCESS (SIGSEGV)' },
      { value: '10', label: 'EXC_BAD_ACCESS (SIGBUS)' },
      { value: '5', label: 'EXC_BREAKPOINT (SIGTRAP)' },
      { value: '4', label: 'EXC_BAD_INSTRUCTION (SIGILL)' },
      { value: '6', label: 'EXC_CRASH (SIGABRT)' },
      { value: '9', label: 'EXC_CRASH (SIGKILL)' },
      { value: '3', label: 'EXC_CRASH (SIGQUIT)' },
    ],
    [FieldKey.OrderStatus]: [
      { value: FIELD_ALL, label: i18n.t('全部') },
      { value: '1', label: i18n.t('已支付') },
      { value: '2', label: i18n.t('待支付') },
      { value: '3', label: i18n.t('已过期') },
    ],
    [FieldKey.ResourceStatus]: [
      { value: ResourceStatus.ALL, label: i18n.t('全部资源包') },
      { value: ResourceStatus.VALID, label: i18n.t('有效资源包') },
      { value: ResourceStatus.INVALID, label: i18n.t('无效资源包') },
    ],
    [FieldKey.BindingStatus]: [
      { value: BindingStatus.BINDINGSTATUSALL, label: i18n.t('全部') },
      { value: BindingStatus.UNBINDING, label: i18n.t('待绑定') },
      { value: BindingStatus.BINDING, label: i18n.t('已绑定') },
    ],
  };
  return fields[fieldKey];
};

export const getIssueTypeMapLabel = (key?: number | null): string => [
  { value: IssueType.ISSUE_TYPE_ELECTRON_CRASH, label: i18n.t('Electron-崩溃列表') },
  { value: IssueType.ISSUE_TYPE_CRASH, label: i18n.t('崩溃-问题列表') },
  { value: IssueType.ISSUE_TYPE_ERROR, label: i18n.t('错误-问题列表') },
  { value: IssueType.ISSUE_TYPE_ANR, label: i18n.t('ANR-问题列表') },
  { value: IssueType.ISSUE_TYPE_FOOM_BUGLY, label: i18n.t('OOM-问题列表') },
  { value: IssueType.ISSUE_TYPE_FOOM, label: i18n.t('FOOM-问题列表') },
  { value: IssueType.ISSUE_TYPE_LAG, label: i18n.t('卡顿-问题列表') },
  { value: IssueType.ISSUE_TYPE_MEM_LEAK, label: isAndroid() ? 'Java 内存泄漏' : 'VC 内存泄漏' },
  { value: IssueType.ISSUE_TYPE_FD_LEAK, label: i18n.t('FD 详情') },
  { value: IssueType.ISSUE_TYPE_NATIVE_MEM, label: i18n.t('Native 内存详情') },
  { value: IssueType.ISSUE_TYPE_BITMAP, label: i18n.t('大图分析') },
  { value: IssueType.ISSUE_TYPE_MEMORY_DETAIL, label: i18n.t('内存详情') },
  { value: IssueType.ISSUE_TYPE_BADMALLOC, label: i18n.t('大块内存分配') },
  { value: IssueType.ISSUE_TYPE_LAUNCH, label: i18n.t('启动-启动个例') },
  { value: IssueType.ISSUE_TYPE_NETWORK_HTTP, label: i18n.t('网络-HTTP') },
  { value: IssueType.ISSUE_TYPE_NETWORK_ERROR, label: i18n.t('网络-网络错误') },
  { value: IssueType.ISSUE_TYPE_TRAFFIC_APART_INNER, label: i18n.t('流量-异常分析') },
  { value: IssueType.ISSUE_TYPE_RUM_JS, label: isHippy() ? 'JS错误' : '错误监控' },
  { value: IssueType.ISSUE_TYPE_RUM_API, label: isHippy() ? 'CGI监控' : 'API监控' },
  { value: IssueType.ISSUE_TYPE_RUM_HIPPY_PERFORAMANCE, label: i18n.t('页面性能') },
  { value: IssueType.ISSUE_TYPE_RUM_PV, label: i18n.t('页面访问') },
  { value: IssueType.ISSUE_TYPE_RUM_EVENT, label: i18n.t('自定义事件') },
  { value: IssueType.ISSUE_TYPE_RUM_CUSTOM, label: i18n.t('自定义测速') },
  { value: IssueType.ISSUE_TYPE_RUM_STATIC, label: i18n.t('静态资源') },
  { value: IssueType.ISSUE_TYPE_RUM_PERFORAMANCE_LAUNCH, label: i18n.t('启动性能') },
  { value: IssueType.ISSUE_TYPE_RUM_PERFORAMANCE_PAGE, label: i18n.t('页面性能') },
  { value: IssueType.ISSUE_TYPE_RUM_PERFORAMANCE_RUNNING, label: i18n.t('运行性能') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_WHITE_SCREEN, label: i18n.t('白屏检测') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_PV, label: i18n.t('页面访问') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_STATIC, label: i18n.t('静态资源') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_SESSION, label: i18n.t('会话追踪') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_EVENT, label: i18n.t('自定义事件') },
  { value: IssueType.ISSUE_TYPE_ELECTRON_CUSTOM, label: i18n.t('自定义测速') },
].find(item => item.value === key)?.label || '';

export enum IOSReasonEnum {
  MONITOR_UNABLE=15,
  TERMINATE=16,
  NORMAL_CRASH=17,
  DEVICE_REBOOT=18,
  APP_UPDATE=19,
  DEADLOCK=20,
  DEADLOCKDEAD=21,
  LAUNCH=22,
  LAUNCH_DEADLOCK=23,
  BOOM=24,
  FOOM=25,
  KB_LAUNCH_QUIT=26,
  UNKNOWN=0,
}

export enum ReasonEnum {
  REASON_ANR=6,
  REASON_CRASH=4,
  REASON_CRASH_NATIVE=5,
  REASON_DEPENDENCY_DIED=12,
  REASON_EXCESSIVE_RESOURCE_USAGE=9,
  REASON_EXIT_SELF=1,
  REASON_FREEZER=14,
  REASON_INITIALIZATION_FAILURE=7,
  REASON_LOW_MEMORY=3,
  REASON_OTHER=13,
  REASON_PERMISSION_CHANGE=8,
  REASON_SIGNALED=2,
  REASON_UNKNOWN=0,
  REASON_USER_REQUESTED=10,
  REASON_USER_STOPPED=11,
}
// 鸿蒙退出原因枚举
export const enum HarmonyExitReasonEnum {
  UNKNOWN=0,
  ABILITY_NOT_RESPONDING=1,
  NORMAL=2,
  CPP_CRASH=3,
  JS_ERROR=4,
  APP_FREEZE=5,
  PERFORMANCE_CONTROL=6,
  RESOURCE_CONTROL=7,
  UPGRADE=8,
  USER_REQUEST=9,
  SIGNAL=10,
}

// 鸿蒙退出原因下拉项
export const HARMONY_EXIT_REASON_OPTION = [
  { value: HarmonyExitReasonEnum.UNKNOWN, label: 'UNKNOWN' },
  { value: HarmonyExitReasonEnum.ABILITY_NOT_RESPONDING, label: 'ABILITY_NOT_RESPONDING' },
  { value: HarmonyExitReasonEnum.NORMAL, label: 'NORMAL' },
  { value: HarmonyExitReasonEnum.CPP_CRASH, label: 'CPP_CRASH' },
  { value: HarmonyExitReasonEnum.JS_ERROR, label: 'JS_ERROR' },
  { value: HarmonyExitReasonEnum.APP_FREEZE, label: 'APP_FREEZE' },
  { value: HarmonyExitReasonEnum.PERFORMANCE_CONTROL, label: 'PERFORMANCE_CONTROL' },
  { value: HarmonyExitReasonEnum.RESOURCE_CONTROL, label: 'RESOURCE_CONTROL' },
  { value: HarmonyExitReasonEnum.UPGRADE, label: 'UPGRADE' },
  { value: HarmonyExitReasonEnum.USER_REQUEST, label: 'USER_REQUEST' },
  { value: HarmonyExitReasonEnum.SIGNAL, label: 'SIGNAL' },
];

// 鸿蒙退出原因
export const HARMONY_EXIT_REASON = {
  [HarmonyExitReasonEnum.UNKNOWN]: hintIconText('UNKNOWN', HARMONY_EXIT_REASON_UNKNOWN_TIPS),
  [HarmonyExitReasonEnum.ABILITY_NOT_RESPONDING]: hintIconText('ABILITY_NOT_RESPONDING', HARMONY_EXIT_REASON_ABILITY_NOT_RESPONDING_TIPS),
  [HarmonyExitReasonEnum.NORMAL]: hintIconText('NORMAL', HARMONY_EXIT_REASON_NORMAL_TIPS),
  [HarmonyExitReasonEnum.CPP_CRASH]: hintIconText('CPP_CRASH', HARMONY_EXIT_REASON_CPP_CRASH_TIPS),
  [HarmonyExitReasonEnum.JS_ERROR]: hintIconText('JS_ERROR', HARMONY_EXIT_REASON_JS_ERROR_TIPS, undefined, undefined, 700),
  [HarmonyExitReasonEnum.APP_FREEZE]: hintIconText('APP_FREEZE', HARMONY_EXIT_REASON_APP_FREEZE_TIPS),
  [HarmonyExitReasonEnum.PERFORMANCE_CONTROL]: hintIconText('PERFORMANCE_CONTROL', HARMONY_EXIT_REASON_PERFORMANCE_CONTROL_TIPS),
  [HarmonyExitReasonEnum.RESOURCE_CONTROL]: hintIconText('RESOURCE_CONTROL', HARMONY_EXIT_REASON_RESOURCE_CONTROL_TIPS),
  [HarmonyExitReasonEnum.UPGRADE]: hintIconText('UPGRADE', HARMONY_EXIT_REASON_UPGRADE_TIPS),
  [HarmonyExitReasonEnum.USER_REQUEST]: hintIconText('USER_REQUEST', HARMONY_EXIT_REASON_USER_REQUEST_TIPS),
  [HarmonyExitReasonEnum.SIGNAL]: hintIconText('SIGNAL', HARMONY_EXIT_REASON_SIGNAL_TIPS),
};

export const HARMONY_EXIT_REASON_STR = {
  [HarmonyExitReasonEnum.UNKNOWN]: 'UNKNOWN',
  [HarmonyExitReasonEnum.ABILITY_NOT_RESPONDING]: 'ABILITY_NOT_RESPONDING',
  [HarmonyExitReasonEnum.NORMAL]: 'NORMAL',
  [HarmonyExitReasonEnum.CPP_CRASH]: 'CPP_CRASH',
  [HarmonyExitReasonEnum.JS_ERROR]: 'JS_ERROR',
  [HarmonyExitReasonEnum.APP_FREEZE]: 'APP_FREEZE',
  [HarmonyExitReasonEnum.PERFORMANCE_CONTROL]: 'PERFORMANCE_CONTROL',
  [HarmonyExitReasonEnum.RESOURCE_CONTROL]: 'RESOURCE_CONTROL',
  [HarmonyExitReasonEnum.UPGRADE]: 'UPGRADE',
  [HarmonyExitReasonEnum.USER_REQUEST]: 'USER_REQUEST',
  [HarmonyExitReasonEnum.SIGNAL]: 'SIGNAL',
};

export const HARMONY_EXIT_OPTION = [
  { value: `${HarmonyExitReasonEnum.UNKNOWN}`, label: 'UNKNOWN' },
  { value: `${HarmonyExitReasonEnum.ABILITY_NOT_RESPONDING}`, label: 'ABILITY_NOT_RESPONDING' },
  { value: `${HarmonyExitReasonEnum.NORMAL}`, label: 'NORMAL' },
  { value: `${HarmonyExitReasonEnum.CPP_CRASH}`, label: 'CPP_CRASH' },
  { value: `${HarmonyExitReasonEnum.JS_ERROR}`, label: 'JS_ERROR' },
  { value: `${HarmonyExitReasonEnum.APP_FREEZE}`, label: 'APP_FREEZE' },
  { value: `${HarmonyExitReasonEnum.PERFORMANCE_CONTROL}`, label: 'PERFORMANCE_CONTROL' },
  { value: `${HarmonyExitReasonEnum.RESOURCE_CONTROL}`, label: 'RESOURCE_CONTROL' },
  { value: `${HarmonyExitReasonEnum.UPGRADE}`, label: 'UPGRADE' },
  { value: `${HarmonyExitReasonEnum.USER_REQUEST}`, label: 'USER_REQUEST' },
  { value: `${HarmonyExitReasonEnum.SIGNAL}`, label: 'SIGNAL' },
];

export enum ExitImportanceEnum {
  IMPORTANCE_BACKGROUND=400,
  IMPORTANCE_CANT_SAVE_STATE=350,
  IMPORTANCE_EMPTY=500,
  IMPORTANCE_FOREGROUND=100,
  IMPORTANCE_FOREGROUND_SERVICE=125,
  IMPORTANCE_GONE=1000,
  IMPORTANCE_PERCEPTIBLE=230,
  IMPORTANCE_PERCEPTIBLE_PRE_26=130,
  IMPORTANCE_SERVICE=300,
  IMPORTANCE_TOP_SLEEPING=325,
  IMPORTANCE_TOP_SLEEPING_PRE_28=150,
  IMPORTANCE_VISIBLE=200,
  PROVIDER_IN_USE=1,
  SERVICE_IN_USE=2,
  UNKNOWN=0
}
const enum ProcessStateEnum {
  STATE_CREATE = 0,
  STATE_FOREGROUND = 1,
  STATE_ACTIVE = 2,
  STATE_BACKGROUND = 3,
  STATE_DESTROY = 4
}

export const PROCESS_STATE_OPTION = [
  { value: ProcessStateEnum.STATE_CREATE, label: 'STATE_CREATE' },
  { value: ProcessStateEnum.STATE_FOREGROUND, label: 'STATE_FOREGROUND' },
  { value: ProcessStateEnum.STATE_ACTIVE, label: 'STATE_ACTIVE' },
  { value: ProcessStateEnum.STATE_BACKGROUND, label: 'STATE_BACKGROUND' },
  { value: ProcessStateEnum.STATE_DESTROY, label: 'STATE_DESTROY' },
];

export const EXIT_IMPORTANCE_OPTION = [
  { value: ExitImportanceEnum.IMPORTANCE_BACKGROUND, label: i18n.t('后台 - IMPORTANCE_BACKGROUND') },
  { value: ExitImportanceEnum.IMPORTANCE_CANT_SAVE_STATE, label: i18n.t('后台 - IMPORTANCE_CANT_SAVE_STATE') },
  { value: ExitImportanceEnum.IMPORTANCE_EMPTY, label: i18n.t('后台 - IMPORTANCE_EMPTY') },
  { value: ExitImportanceEnum.IMPORTANCE_FOREGROUND, label: i18n.t('前台 - IMPORTANCE_FOREGROUND') },
  { value: ExitImportanceEnum.IMPORTANCE_FOREGROUND_SERVICE, label: i18n.t('前台 - IMPORTANCE_FOREGROUND_SERVICE') },
  { value: ExitImportanceEnum.IMPORTANCE_GONE, label: i18n.t('后台 - IMPORTANCE_GONE') },
  { value: ExitImportanceEnum.IMPORTANCE_PERCEPTIBLE, label: i18n.t('后台 - IMPORTANCE_PERCEPTIBLE') },
  { value: ExitImportanceEnum.IMPORTANCE_PERCEPTIBLE_PRE_26, label: i18n.t('后台 - IMPORTANCE_PERCEPTIBLE_PRE_26') },
  { value: ExitImportanceEnum.IMPORTANCE_SERVICE, label: i18n.t('后台 - IMPORTANCE_SERVICE') },
  { value: ExitImportanceEnum.IMPORTANCE_TOP_SLEEPING, label: i18n.t('后台 - IMPORTANCE_TOP_SLEEPING') },
  { value: ExitImportanceEnum.IMPORTANCE_TOP_SLEEPING_PRE_28, label: i18n.t('后台 - IMPORTANCE_TOP_SLEEPING_PRE_28') },
  { value: ExitImportanceEnum.IMPORTANCE_VISIBLE, label: i18n.t('前台 - IMPORTANCE_VISIBLE') },
  { value: ExitImportanceEnum.PROVIDER_IN_USE, label: i18n.t('后台 - PROVIDER_IN_USE') },
  { value: ExitImportanceEnum.SERVICE_IN_USE, label: i18n.t('后台 - SERVICE_IN_USE') },
  { value: ExitImportanceEnum.UNKNOWN, label: 'UNKNOWN' },
];
export const EXIT_IMPORTANCE = {
  [ExitImportanceEnum.IMPORTANCE_BACKGROUND]: hintText('后台 - IMPORTANCE_BACKGROUND', IMPORTANCE_BACKGROUND_TIPS),
  [ExitImportanceEnum.IMPORTANCE_CANT_SAVE_STATE]: hintText('后台 - IMPORTANCE_CANT_SAVE_STATE', IMPORTANCE_CANT_SAVE_STATE_TIPS),
  [ExitImportanceEnum.IMPORTANCE_EMPTY]: hintText('后台 - IMPORTANCE_EMPTY', IMPORTANCE_EMPTY_TIPS),
  [ExitImportanceEnum.IMPORTANCE_FOREGROUND]: hintText('前台 - IMPORTANCE_FOREGROUND', IMPORTANCE_FOREGROUND_TIPS),
  [ExitImportanceEnum.IMPORTANCE_FOREGROUND_SERVICE]: hintText('前台 - IMPORTANCE_FOREGROUND_SERVICE', IMPORTANCE_FOREGROUND_SERVICE_TIPS),
  [ExitImportanceEnum.IMPORTANCE_GONE]: hintText('后台 - IMPORTANCE_GONE', IMPORTANCE_GONE_TIPS),
  [ExitImportanceEnum.IMPORTANCE_PERCEPTIBLE]: hintText('后台 - IMPORTANCE_PERCEPTIBLE', IMPORTANCE_PERCEPTIBLE_TIPS),
  [ExitImportanceEnum.IMPORTANCE_PERCEPTIBLE_PRE_26]: hintText('后台 - IMPORTANCE_PERCEPTIBLE_PRE_26', IMPORTANCE_PERCEPTIBLE_PRE_26_TIPS),
  [ExitImportanceEnum.IMPORTANCE_SERVICE]: hintText('后台 - IMPORTANCE_SERVICE', IMPORTANCE_SERVICE_TIPS),
  [ExitImportanceEnum.IMPORTANCE_TOP_SLEEPING]: hintText('后台 - IMPORTANCE_TOP_SLEEPING', IMPORTANCE_TOP_SLEEPING_TIPS),
  [ExitImportanceEnum.IMPORTANCE_TOP_SLEEPING_PRE_28]: hintText('后台 - IMPORTANCE_TOP_SLEEPING_PRE_28', IMPORTANCE_TOP_SLEEPING_PRE_28_TIPS),
  [ExitImportanceEnum.IMPORTANCE_VISIBLE]: hintText('前台 - IMPORTANCE_VISIBLE', IMPORTANCE_VISIBLE_TIPS),
  [ExitImportanceEnum.PROVIDER_IN_USE]: hintText('后台 - PROVIDER_IN_USE', PROVIDER_IN_USE_TIPS),
  [ExitImportanceEnum.SERVICE_IN_USE]: hintText('后台 - SERVICE_IN_USE', SERVICE_IN_USE_TIPS),
  [ExitImportanceEnum.UNKNOWN]: hintText('UNKNOWN', UNKNOWN_TIPS),
};
// 鸿蒙进程状态
export const PROCESS_STATE = {
  [ProcessStateEnum.STATE_CREATE]: hintText('STATE_CREATE', STATE_CREATE_TIPS),
  [ProcessStateEnum.STATE_FOREGROUND]: hintText('STATE_FOREGROUND', STATE_FOREGROUND_TIPS),
  [ProcessStateEnum.STATE_ACTIVE]: hintText('STATE_ACTIVE', STATE_ACTIVE_TIPS),
  [ProcessStateEnum.STATE_BACKGROUND]: hintText('STATE_BACKGROUND', STATE_BACKGROUND_TIPS),
  [ProcessStateEnum.STATE_DESTROY]: hintText('STATE_DESTROY', STATE_DESTROY_TIPS),
};

export const REASON = {
  [ReasonEnum.REASON_ANR]: 'REASON_ANR',
  [ReasonEnum.REASON_CRASH]: 'REASON_CRASH',
  [ReasonEnum.REASON_CRASH_NATIVE]: 'REASON_CRASH_NATIVE',
  [ReasonEnum.REASON_DEPENDENCY_DIED]: 'REASON_DEPENDENCY_DIED',
  [ReasonEnum.REASON_EXCESSIVE_RESOURCE_USAGE]: 'REASON_EXCESSIVE_RESOURCE_USAGE',
  [ReasonEnum.REASON_EXIT_SELF]: 'REASON_EXIT_SELF',
  [ReasonEnum.REASON_FREEZER]: 'REASON_FREEZER',
  [ReasonEnum.REASON_INITIALIZATION_FAILURE]: 'REASON_INITIALIZATION_FAILURE',
  [ReasonEnum.REASON_LOW_MEMORY]: 'REASON_LOW_MEMORY',
  [ReasonEnum.REASON_OTHER]: 'REASON_OTHER',
  [ReasonEnum.REASON_PERMISSION_CHANGE]: 'REASON_PERMISSION_CHANGE',
  [ReasonEnum.REASON_SIGNALED]: 'REASON_SIGNALED',
  [ReasonEnum.REASON_UNKNOWN]: 'REASON_UNKNOWN',
  [ReasonEnum.REASON_USER_REQUESTED]: 'REASON_USER_REQUESTED',
  [ReasonEnum.REASON_USER_STOPPED]: 'REASON_USER_STOPPED',
};


export const ARCHITECTURE = {
  [Architecture.ARM32]: 'ARM_32',
  [Architecture.ARM64]: 'ARM_64',
  [Architecture.X86]: 'X86',
  [Architecture.X86_64]: 'X86_64',
  [Architecture.RISCV64]: 'RISCV64',

};


export const getLocalFieldLabelByValue = (field: FieldKey, value: string) => (
  getLocalField(field)?.find(item => item.value === value)?.label
);

export const getLaunch = (type?: string | null): string => (isIOS() || isVisionOS() || isAppleTV() ? [
  { value: LaunchType.Cold, label: i18n.t('冷启动（非 prewarm）') },
  { value: LaunchType.ColdPrewarm, label: i18n.t('冷启动（prewarm）') },
  { value: LaunchType.Warm, label: i18n.t('后台切前台') },
] : [{ value: LaunchType.Cold, label: i18n.t('冷启动') },
  { value: LaunchType.Warm, label: i18n.t('温启动') }]).find(item => item.value === type)?.label || '';


export const IOS_ATTACH_FILE_OPTIONS = [
  { value: FIELD_ALL, label: i18n.t('全部') },
  { value: 'meminfo.txt', label: 'meminfo.txt' },
  { value: 'user_datas.log', label: 'user_datas.log' },
  { value: 'valueMapOthers.txt', label: 'valueMapOthers.txt' },
  { value: 'crash_attach.log', label: 'crash_attach.log' },
  { value: 'buglylog.txt', label: 'buglylog.txt' },
  { value: 'martianlog.txt', label: 'martianlog.txt' },
  { value: 'crash.log', label: 'crash.log' },
];
export const ANDROID_ATTACH_FILE_OPTIONS = [
  { value: FIELD_ALL, label: i18n.t('全部') },
  { value: 'tomb.zip', label: 'tomb.zip' },
  { value: 'jniLog.txt', label: 'jniLog.txt' },
  { value: 'user_datas.log', label: 'user_datas.log' },
  { value: 'valueMapOthers.txt', label: 'valueMapOthers.txt' },
  { value: 'crashInfos.txt', label: 'crashInfos.txt' },
  { value: 'alltimes.txt', label: 'alltimes.txt' },
  { value: 'log.txt', label: 'log.txt' },
];

// filter里用
export enum CommonFieldKey {
  ServerIsp = 'server_isp',
  ClientIsp = 'client_isp',
  CustomKey = 'custom_key',
  FilePath = 'file_path',
  AppInForeground = 'app_in_foreground',
  ResourceType = 'resource_type',
  AbilityName='ability_name',
  ProcessState='process_state',
  FeatureShow='feature_show',
  P50MaxMallocSize='p50_max_malloc_size',
  P90MaxMallocSize='p90_max_malloc_size',
  MaxMallocSize='max_malloc_size',
  AvgMaxMallocSize='avg_max_malloc_size',
  IsInferenceFeature='is_inference_feature',
  OriginalStacks='original_stacks',
  Linkages='linkages',
  MallocType='malloc_type',
  UserClassifyData='user_classify_data',
  NetworkType='network_type',
  AppInBack='app_in_back',
  SpanCostTime='span_cost_time',
  LaunchCost='launch_cost',
  LaunchTags='launch_tags',
  Operator='operator',
  ParentTrafficSize='parent_traffic_size',
  CollectType = 'collect_type',
  ChannelId='channel_id',
  SysCpuUsage='sys_cpu_usage',
  AppCpuUsage='app_cpu_usage',
  CpuArchitecture = 'cpu_architecture',
  EventType = 'event_type',
  FullOsVersion = 'full_os_version',
  ActivePrewarm = 'active_prewarm',
  BuildNumber = 'build_number',
  Stack = 'stack',
  TranslatedStacks='translated_stacks',
  TrafficSize = 'traffic_size',
  DetectStrategy = 'detect_strategy',
  TimeCost = 'time_cost',
  SystemStack ='system_stack',
  MonitoredThreadName = 'monitored_thread_name',
  ExceptionType = 'exception_type',
  BasicExceptionType='basic_exception_type',
  FuncName = 'func_name',
  Platform = 'platform',
  tagList = 'tag_list',
  Tags= 'tags',
  TapdHandler = 'tapd_handler',
  TapdStatus = 'tapd_status',
  IssueStatus = 'issue_status',
  ObjectSize = 'object_size',
  VmMaxSize = 'vm_max_size',
  ObjectType = 'object_type',
  TopNum = 'top_num',
  BizExtendInfo = 'biz_extend_info',
  Feature = 'feature',
  ClientIdentify = 'client_identify',
  Ground = 'ground',
  AppVersionMode = 'app_version_mode',
  TimeRange = 'time_range',
  AppVersion = 'app_version',
  OsVersion = 'full_os_version',
  OldOsVersion = 'os_version',
  Brand = 'brand',
  BundleId = 'bundle_id',
  Span = 'span_name',
  Model = 'model',
  SdkVersion = 'sdk_version',
  bizExtendInfo = 'biz_extend_info',
  UserCustom = 'user_custom',
  ProcessName = 'process_name',
  ThreadName = 'thread_name',
  Qimei = 'qimei',
  AccountId = 'account_id',
  LaunchId = 'launch_id',
  ProcessLaunchId = 'process_launch_id',
  TagsInFilter = 'tags_in_filter',
  ShiplyTag = 'shiply_tag',
  AbTestTag = 'ab_test_tag',
  Stage = 'stage',
  ShiplyAbTestTag = 'shiply_ab_test_tag',
  JavaStage = 'java_stage',
  PssStage = 'pss_stage',
  VssStage = 'vss_stage',
  FgMemStage='fg_mem_stage',
  Rom='rom_name',
  LaunchType = 'launch_type',
  StandardInferredLabels = 'inferred_labels',
  DumpReason='dump_reason',
  DocCount='doc_count',
  QimeiCount='qimei_count',
  LeakObjectType='object_link_type',
  LaunchDuration = 'launch_duration',
  HprofFileStatus = 'hprof_file_status',
  CpuCores = 'cpu_cores',
  ElectronCpuArch = 'arch',
  ModelName = 'model_name',
  FreeMem = 'free_mem',
  MainStack = 'main_stack',
  GraphicLevelMask = 'graphic_level_mask',
  GraphicLevelMap='graphic_level_map',
  SceneName = 'scene_name',
  // 性能指标字段
  FpsAvg = 'fps_avg',
  FpsStutter = 'fps_stutter',
  OneLowFrameBase = 'one_low_frame_base',
  OneLowTimeBase = 'one_low_time_base',
  JankCount = 'jank_count',
  BigJankCount = 'big_jank_count',
  PssMemPeak = 'pss_mem_peak',
  UsedMem='used_mem',
  // Metrickit FOOM 相关
  Version = 'app_version',
  BuildNo = 'build_number',
  Device = 'device',
  MetrickitOsVersion = 'os_version',
  AssociatedLaunchDuration = 'associated_launch_duration',
  AssociatedMemorySize = 'associated_memory_size',
  AssociatedTimeDiff = 'associated_time_diff',
  Signal = 'signal',
  TerminationReason = 'termination_reason',
  CrashScenario = 'crash_scenario',
  AssociatedFeature = 'associated_feature',
}

// filter外面用, 带上standard 前缀，避免触发后台处理filter里面字段和外面字段的兼容逻辑
export enum StandardCommonFieldKey {
  FilePath='standard_file_path',
  AppInForeground = 'standard_app_in_foreground',
  ResourceType = 'standard_resource_type',
  AbilityName='standard_ability_name',
  ProcessState = 'standard_process_state',
  P50MaxMallocSize='standard_p50_max_malloc_size',
  P90MaxMallocSize='standard_p90_max_malloc_size',
  MaxMallocSize='standard_max_malloc_size',
  AvgMaxMallocSize='standard_avg_max_malloc_size',
  IsInferenceFeature='standard_is_inference_feature',
  OriginalStacks='standard_original_stacks',
  Linkages='standard_linkages',
  UserClassifyData='standard_user_classify_data',
  AppInBack='standard_app_in_back',
  SpanCostTime='standard_span_cost_time',
  Operator='standard_operator',
  ClientOperator='standard_client_operator',
  ServerOperator='standard_server_operator',
  ParentTrafficSize='standard_parent_traffic_size',
  CollectType='standard_collect_type',
  SysCpuUsage='standard_sys_cpu_usage',
  AppCpuUsage='standard_app_cpu_usage',
  Area = 'standard_area',
  ServerArea = 'standard_server_area',
  CpuCores = 'standard_cpu_cores',
  CpuArchitecture = 'standard_cpu_architecture',
  ElectronCpuArch = 'standard_arch',
  EventType = 'standard_event_type',
  SpanCost='standard_span_cost',
  LaunchCost='standard_launch_cost',
  LaunchTags= 'standard_launch_tags',
  LaunchType = 'standard_launch_type',
  ActivePrewarm = 'standard_active_prewarm',
  LeakObjectType = 'standard_object_link_type',
  DumpReason = 'standard_dump_reason',
  DocCount ='standard_doc_count',
  QimeiCount='standard_qimei_count',
  StandardInferredLabels = 'standard_inferred_labels',
  BuildNumber = 'standard_build_number',
  Stack = 'standard_stack',
  DetectStrategy = 'standard_detect_strategy',
  TimeCost = 'standard_time_cost',
  SystemStack = 'standard_system_stack',
  MonitoredThreadName='standard_monitored_thread_name',
  ExceptionType = 'standard_exception_type',
  BasicExceptionType='standard_basic_exception_type',
  FuncName = 'standard_func_name',
  Platform = 'standard_platform',
  TagList='standard_tags_list',
  Tags = 'standard_tags',
  TapdHandler = 'standard_tapd_handler',
  TapdStatus = 'standard_tapd_status',
  IssueStatus='standard_issue_status',
  ObjectSize='standard_object_size',
  VmMaxSize = 'standard_vm_max_size',
  ObjectType = 'standard_object_type',
  TopNum = 'standard_top_num',
  BizExtendInfo = 'standard_biz_extend_info',
  CustomKey = 'standard_custom_key',
  Feature = 'standard_feature',
  ClientIdentify = 'standard_client_identify',
  Ground = 'standard_ground',
  AppVersionMode = 'standard_app_version_mode',
  TimeRange = 'standard_time_range',
  AppVersion = 'standard_app_version',
  OsVersion = 'standard_os_version',
  FullOsVersion = 'standard_full_os_version',
  Brand = 'standard_brand',
  BundleId = 'standard_bundle_id',
  ChannelId='standard_channel_id',
  Span = 'standard_span_name',
  Model = 'standard_model',
  SdkVersion = 'standard_sdk_version',
  bizExtendInfo = 'standard_biz_extend_info',
  UserCustom = 'standard_user_custom',
  ProcessName = 'standard_process_name',
  JavaStage = 'standard_java_stage',
  FgMemStage='standard_fg_mem_stage',
  PssStage = 'standard_pss_stage',
  VssStage = 'standard_vss_stage',
  ThreadName = 'standard_thread_name',
  Qimei = 'standard_qimei',
  AccountId = 'standard_account_id',
  LaunchId = 'standard_launch_id',
  ProcessLaunchId = 'standard_process_launch_id',
  TagsInFilter = 'standard_tags_in_filter',
  ShiplyTag = 'standard_shiply_tag',
  AbTestTag = 'standard_ab_test_tag',
  Stage = 'standard_stage',
  ShiplyAbTestTag = 'standard_shiply_ab_test_tag',
  Rom='standard_rom_name',
  StandardLaunchDuration = 'standard_launch_duration',
  FreeMem = 'standard_free_mem',
  NetWorkType = 'standard_network_type',
  LlmModel = 'standard_model_name',
  MainStack='standard_main_stack',
  GraphicLevelMask ='standard_graphic_level_mask',
  SceneName = 'standard_scene_name',
  // 性能指标字段
  FpsAvg = 'standard_fps_avg',
  FpsStutter = 'standard_fps_stutter',
  OneLowFrameBase = 'standard_one_low_frame_base',
  OneLowTimeBase = 'standard_one_low_time_base',
  JankCount = 'standard_jank_count',
  BigJankCount = 'standard_big_jank_count',
  PssMemPeak = 'standard_pss_mem_peak',
  GraphicLevelMark='standard_graphic_level_mark',
  UsedMem= 'standard_used_mem',
  // Metrickit FOOM 相关
  Version = 'standard_app_version',
  BuildNo = 'standard_build_number',
  Device = 'standard_device',
  MetrickitOsVersion = 'standard_os_version',
  AssociatedLaunchDuration = 'standard_associated_launch_duration',
  AssociatedMemorySize = 'standard_associated_memory_size',
  AssociatedTimeDiff = 'standard_associated_time_diff',
  AssociatedFeature = 'standard_associated_feature',
  Signal = 'standard_signal',
  TerminationReason = 'standard_termination_reason',
  CrashScenario = 'standard_crash_scenario',
}


export const APP_IN_BACK_OPTIONS = [
  { label: i18n.t('前台'), value: 'false' },
  { label: i18n.t('后台'), value: 'true' },
];
